import { useEffect, useState } from "react";
import { FaEye, FaEyeSlash, FaRegTrashAlt } from "react-icons/fa";
import { FcAddImage } from "react-icons/fc";

export default function ClientInputFieldZod({
  label,
  name,
  type,
  register,
  errors,
  setValue,
  resetImages,
  autoComplete = "off",
  value = undefined,
  required = false,
  className = "",
  placeholder = "",
  price = "",
  rows = 0,
  min = 0,
  options = [],
  childClassName = "",
  direction = "rtl",
  pull = false,
  disabled = false,
}) {
  const [showPassword, setShowPassword] = useState(false);
  const [selectedImages, setSelectedImages] = useState([]);
  const [fileArray, setFileArray] = useState([]);
  const wrapperClass = `${className} ${pull ? "md:col-span-2" : ""}`;
  const errorMessage = errors[name]?.message;

  const onSelectFile = (event) => {
    const selectedFilesArray = Array.from(event.target.files || []);
    setFileArray((previousFiles) => previousFiles.concat(selectedFilesArray));
    const imagesArray = selectedFilesArray.map((file) =>
      URL.createObjectURL(file),
    );
    setSelectedImages((previousImages) => previousImages.concat(imagesArray));
    setValue(name, [...fileArray, ...selectedFilesArray]);
  };

  const removeImage = (imageIndex) => {
    const newSelectedImages = selectedImages.filter(
      (_, index) => index !== imageIndex,
    );
    const newFileArray = fileArray.filter((_, index) => index !== imageIndex);
    setSelectedImages(newSelectedImages);
    setFileArray(newFileArray);
    setValue(name, newFileArray);
  };

  useEffect(() => {
    if (resetImages) {
      setSelectedImages([]);
      setFileArray([]);
    }
  }, [resetImages]);

  const renderError = () =>
    errorMessage ? (
      <p className="mt-1 text-xs text-center text-rose-400">{errorMessage}</p>
    ) : null;

  if (type === "select") {
    return (
      <div className={wrapperClass}>
        <label
          htmlFor={name}
          className="text-sm font-medium"
          style={{ color: "var(--layout-text)" }}
        >
          {label}
        </label>
        {required ? <span className="font-bold text-red-600">*</span> : null}
        <select
          {...register(name)}
          id={name}
          autoComplete="off"
          defaultValue=""
          className={`portal-input mt-2 block h-11 w-full px-4 py-2.5 text-sm ${
            errorMessage ? "border-rose-500/60" : ""
          }`}
        >
          <option value="">{label}</option>
          {options.length > 0 && typeof options[0] === "object"
            ? options.map((selector) => (
                <option key={selector.id} value={selector.id}>
                  {selector.title || selector.name}
                </option>
              ))
            : options.map((selector, index) => (
                <option key={index} value={selector}>
                  {selector}
                </option>
              ))}
        </select>
        {renderError()}
      </div>
    );
  }

  if (type === "file") {
    return (
      <div className={wrapperClass}>
        <label
          htmlFor={name}
          className="text-sm font-medium"
          style={{ color: "var(--layout-text)" }}
        >
          {label}
        </label>
        {required ? <span className="font-bold text-red-600">*</span> : null}
        <input
          {...register(name)}
          type="file"
          id={name}
          className={`portal-input mt-2 w-full cursor-pointer rounded-xl px-2 py-2 file:ml-3 file:cursor-pointer file:rounded-xl file:border-0 file:bg-[var(--layout-accent)] file:px-3 file:py-2 file:text-white ${
            errorMessage ? "border-rose-500/60" : ""
          }`}
        />
        {renderError()}
      </div>
    );
  }

  if (type === "multi-file") {
    return (
      <div className={wrapperClass}>
        <label
          htmlFor={name}
          className="text-sm font-medium"
          style={{ color: "var(--layout-text)" }}
        >
          {label}
        </label>
        {required ? <span className="font-bold text-red-600">*</span> : null}
        <div className="mt-2 grid grid-cols-1 gap-4 md:grid-cols-4">
          <label className="portal-page-card flex h-44 cursor-pointer flex-col items-center justify-center gap-2 rounded-[1.35rem] p-4 text-center">
            <FcAddImage className="h-16 w-16" />
            <p
              className="text-sm font-semibold"
              style={{ color: "var(--layout-text)" }}
            >
              رفع الصور
            </p>
            <input
              {...register(name)}
              type="file"
              multiple
              id={name}
              hidden
              onChange={onSelectFile}
            />
          </label>

          {selectedImages.map((image, index) => (
            <div
              key={index}
              className="portal-page-card relative overflow-hidden rounded-[1.35rem]"
            >
              <img
                src={image}
                alt={`selected-${index}`}
                className="h-44 w-full rounded-[1.35rem] object-cover"
              />
              <button
                type="button"
                className="absolute right-3 top-3 rounded-full bg-rose-500 p-2 text-white transition hover:bg-rose-600"
                onClick={() => removeImage(index)}
              >
                <FaRegTrashAlt className="h-4 w-4" />
              </button>
            </div>
          ))}
        </div>
        {renderError()}
      </div>
    );
  }

  if (type === "textarea") {
    return (
      <div className={wrapperClass}>
        <label
          htmlFor={name}
          className="text-sm font-medium"
          style={{ color: "var(--layout-text)" }}
        >
          {label}
        </label>
        {required ? <span className="font-bold text-red-600">*</span> : null}
        <textarea
          {...register(name)}
          id={name}
          autoComplete={autoComplete}
          rows={rows}
          placeholder={placeholder}
          className={`portal-input mt-2 block w-full resize-none px-4 py-3 text-sm ${
            errorMessage ? "border-rose-500/60" : ""
          }`}
        >
          {value}
        </textarea>
        {renderError()}
      </div>
    );
  }

  if (type === "radio") {
    return (
      <div className={wrapperClass}>
        <label
          className="text-sm font-medium"
          style={{ color: "var(--layout-text)" }}
        >
          {label}
        </label>
        {required ? <span className="font-bold text-red-600">*</span> : null}
        <div className={`mt-2 grid gap-2 ${childClassName}`}>
          {options.map((option) => (
            <label
              key={option.value}
              htmlFor={`${name}-${option.value}`}
              className="portal-page-card flex items-center gap-3 rounded-xl px-4 py-3"
            >
              <input
                type="radio"
                id={`${name}-${option.value}`}
                value={option.value}
                name={name}
                {...register(name, { required })}
                className="h-4 w-4 accent-[var(--layout-accent)]"
              />
              <span
                className="text-sm font-semibold"
                style={{ color: "var(--layout-text)" }}
              >
                {option.label}
              </span>
            </label>
          ))}
        </div>
        {renderError()}
      </div>
    );
  }

  if (type === "checkbox") {
    return (
      <div className={wrapperClass}>
        <span
          className="text-sm font-medium"
          style={{ color: "var(--layout-text)" }}
        >
          {label}
        </span>
        {required ? <span className="font-bold text-red-600">*</span> : null}
        <div className="mt-2 grid gap-2">
          {options.map((item) => (
            <label
              key={item.id || item.name}
              htmlFor={`${name}-${item.id}`}
              className="portal-page-card flex items-center justify-between rounded-xl px-4 py-3"
            >
              <span
                className="text-sm font-semibold"
                style={{ color: "var(--layout-text)" }}
              >
                {item.name}
              </span>
              <input
                type="checkbox"
                id={`${name}-${item.id}`}
                value={item.name}
                name={`${name}-${item.id}`}
                {...register(name, { required })}
                disabled={disabled}
                className="h-4 w-4 accent-[var(--layout-accent)]"
              />
            </label>
          ))}
        </div>
        {renderError()}
      </div>
    );
  }

  if (type === "price-input") {
    return (
      <div className={wrapperClass}>
        <label
          htmlFor={name}
          className="text-sm font-medium"
          style={{ color: "var(--layout-text)" }}
        >
          {label}
        </label>
        {required ? <span className="font-bold text-red-600">*</span> : null}
        <div className="relative mt-2">
          <span className="portal-accent-bg absolute inset-y-0 left-0 grid w-12 place-items-center rounded-l-xl text-lg font-bold text-white">
            {price}
          </span>
          <input
            {...register(name)}
            type="number"
            id={name}
            autoComplete={autoComplete}
            placeholder={placeholder}
            value={value}
            className={`portal-input block h-11 w-full pl-14 pr-4 text-sm ${
              errorMessage ? "border-rose-500/60" : ""
            }`}
          />
        </div>
        {renderError()}
      </div>
    );
  }

  return (
    <div className={`relative ${wrapperClass}`}>
      <label
        htmlFor={name}
        className="text-sm font-medium"
        style={{ color: "var(--layout-text)" }}
      >
        {label}
      </label>
      {required ? <span className="font-bold text-red-600">*</span> : null}
      <input
        {...register(name)}
        type={
          type === "password" ? (!showPassword ? "password" : "text") : type
        }
        id={name}
        autoComplete={autoComplete}
        placeholder={placeholder}
        value={value}
        min={min}
        disabled={disabled}
        step={type === "number" ? "any" : undefined}
        inputMode={type === "number" ? "decimal" : undefined}
        className={`portal-input mt-2 block h-11 w-full px-4 py-2.5 text-sm ${
          errorMessage ? "border-rose-500/60" : ""
        }`}
        style={{ direction }}
      />

      {type === "password" ? (
        <span
          title="عرض أو إخفاء كلمة المرور"
          onClick={() => setShowPassword((prev) => !prev)}
          className="portal-outline-button absolute top-[37px] right-[7px] cursor-pointer rounded-xl p-2"
        >
          {!showPassword ? (
            <FaEye style={{ color: "var(--layout-text)" }} />
          ) : (
            <FaEyeSlash style={{ color: "var(--layout-text)" }} />
          )}
        </span>
      ) : null}
      {renderError()}
    </div>
  );
}
